<template>
  <div :ref="`slider-${slideshow._id}`" class="slider">
    <swiper class="slides-container" :ref="`slides-container-${slideshow._id}`" :options="slideshowOptions">
      <swiper-slide v-for="slide in slideshow.slides" :key="slide._id">
        <md-progress-spinner class="slider-spinner md-accent" :md-diameter="40" :md-stroke="5" md-mode="indeterminate" :style="{ display: (loading) ? 'flex' : 'none' }" />
        <img :ref="`slide-${slide._id}`" src="../assets/image-default.jpg" />
        <div v-if="!isFullscreen" class="btn-slideshow md-xsmall-hide" style="top:20px" @click="fullscreen(slideshow._id)">
          <md-icon>fullscreen</md-icon>
          <md-tooltip md-direction="left">{{ $t('fullscreen') }}</md-tooltip>
        </div>
        <div v-else class="btn-slideshow md-xsmall-hide" style="top:20px" @click="exitFullscreen()">
          <md-icon>fullscreen_exit</md-icon>
          <md-tooltip md-direction="left">{{ $t('exit_fullscreen') }}</md-tooltip>
        </div>
        <md-menu v-if="!isFullscreen" md-size="small" class="btn-slideshow md-xsmall-hide" style="top:60px">
          <div md-menu-trigger>
            <md-icon>save_alt</md-icon>
            <md-tooltip md-direction="left">{{ $t('download') }}</md-tooltip>
          </div>
          <md-menu-content md-theme="toolbar">

            <div>
              <md-menu-item @click="openFile(slide.image)">
                <md-icon>photo</md-icon>
                <span class="md-list-item-text">Slide</span>
              </md-menu-item>
            </div>

            <div v-for="medium in slideshow.media" :key="medium._id">
              <md-menu-item v-if="medium.pptx" @click="openFile(medium.pptx)">
                <md-icon>photo_library</md-icon>
                <span class="md-list-item-text">PowerPoint</span>
              </md-menu-item>
              <md-menu-item v-else-if="medium.pdf" @click="openFile(medium.pdf)">
                <md-icon>picture_as_pdf</md-icon>
                <span class="md-list-item-text">PDF</span>
              </md-menu-item>
            </div>

          </md-menu-content>
        </md-menu>
      </swiper-slide>
      <div class="swiper-pagination md-xsmall-hide" slot="pagination"></div>
      <div class="swiper-button-prev md-xsmall-hide" :class="navButtonColor" slot="button-prev"></div>
      <div class="swiper-button-next md-xsmall-hide" :class="navButtonColor" slot="button-next"></div>
    </swiper>
    <swiper class="thumbs-container" :ref="`thumbs-container-${slideshow._id}`" :options="slideshowThumbsOptions">
      <swiper-slide v-for="(slide, index) in slideshow.slides" :key="slide._id">
        <img :ref="`thumb-${slide._id}`" src="../assets/image-default.jpg" @click="loadSlide(index)" />
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import _ from 'lodash'
import helpers from '@/services/helpers'
const { openFile } = helpers

export default {
  name: 'Slideshow',
  props: {
    slideshow: Object
  },
  data: () => ({
    showSidebar: false,
    isFullscreen: false,
    loading: true,
    slidesLoaded: [0],
    slideUrls: [],
    slider: '',
    slideshowOptions: {
      init: false,
      pagination: {
        el: '.swiper-pagination'
        // clickable: true
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      // Disable preloading of all images
      preloadImages: false,
      // Enable lazy loading
      lazy: true
    },
    slideshowThumbsOptions: {
      init: false,
      centeredSlides: true,
      slidesPerView: 'auto',
      touchRatio: 0.2,
      slideToClickedSlide: true,
      // Disable preloading of all images
      preloadImages: false,
      // Enable lazy loading
      lazy: true
    }
  }),
  mounted () {
    const $vm = this
    const slides = $vm.slideshow.slides
    const slideshow = $vm.$refs[`slides-container-${$vm.slideshow._id}`].swiper
    const thumbs = $vm.$refs[`thumbs-container-${$vm.slideshow._id}`].swiper
    $vm.slideUrls = slides.map(slide => slide.image)
    const thumbUrls = slides.map(slide => slide.thumb)

    const init = () => {
      const loadThumbs = async () => {
        await new Promise((resolve, reject) => {
          // Check number of thumbs
          // Make bulk download only up to 35 thumbs to prevent too long request uris
          if (thumbUrls.length > 35) {
            _.each(thumbUrls, (url, index) => {
              $vm.$store.dispatch('getFile', url)
                .then(file => {
                  // Set thumb source
                  const $thumbEl = $vm.$refs[`thumb-${slides[index]._id}`][0]
                  $thumbEl.src = file
                })
                .catch(err => reject(err))
            })
            resolve()
          } else {
            $vm.$store.dispatch('getFiles', thumbUrls)
              .then(files => {
                _.each(files, (file, index) => {
                  // Set thumb source
                  const $thumbEl = $vm.$refs[`thumb-${slides[index]._id}`][0]
                  $thumbEl.src = file
                })
                resolve()
              })
              .catch(err => reject(err))
          }
        })
      }
      const loadSlide = async () => {
        await new Promise((resolve, reject) => {
          // Load first slide
          $vm.$store.dispatch('getFile', $vm.slideUrls[0])
            .then(file => {
              // Set image source
              const $slideEl = $vm.$refs[`slide-${slides[0]._id}`][0]
              $slideEl.src = file
              resolve()
            })
            .catch(err => reject(err))
        })
      }

      loadThumbs()
        .then(() => {
          return loadSlide()
        })
        .then(() => {
          $vm.loading = false
        })
        .catch(err => console.error(err))
    }

    // On slideshow init
    slideshow.on('init', () => {
      init()
    })

    // Load slide on slide change
    slideshow.on('slideChange', () => {
      const index = slideshow.activeIndex
      $vm.loadSlide(index, slides)
    })

    // Connect slideshows with each other
    slideshow.controller.control = thumbs
    thumbs.controller.control = slideshow

    // Detect when browser enters or exits fullscreen mode
    window.addEventListener('fullscreenchange', $vm.onFullscreenChange)
    window.addEventListener('webkitfullscreenchange', $vm.onFullscreenChange) /* Chrome, Safari and Opera */
    window.addEventListener('mozfullscreenchange', $vm.onFullscreenChange) /* Firefox */
    window.addEventListener('MSFullscreenChange', $vm.onFullscreenChange) /* IE/Edge */

    // Init the slideshows
    slideshow.init()
    thumbs.init()
  },
  beforeDestroy () {
    const $vm = this
    window.removeEventListener('fullscreenchange', $vm.onFullscreenChange)
    window.removeEventListener('webkitfullscreenchange', $vm.onFullscreenChange) /* Chrome, Safari and Opera */
    window.removeEventListener('mozfullscreenchange', $vm.onFullscreenChange) /* Firefox */
    window.removeEventListener('MSFullscreenChange', $vm.onFullscreenChange) /* IE/Edge */
  },
  computed: {
    navButtonColor: function () { // Black nav buttons on mobile screens
      if (this.$store.state.screenSize === 'xsmall') return { 'swiper-button-black': true }
      return { 'swiper-button-white': true }
    }
  },
  methods: {
    openFile,
    loadSlide (index, slidesArray = null, counter = 0) {
      const $vm = this
      const slides = slidesArray || $vm.slideshow.slides
      // Only load slide when it hasn't been loaded yet
      if (counter || !$vm.slidesLoaded.includes(index)) {
        // Push slide index to previously loaded slides
        if (!counter) $vm.slidesLoaded.push(index)
        // Request image file
        $vm.$store.dispatch('getFile', $vm.slideUrls[index])
          .then(file => {
            // Set image source
            const $slideEl = $vm.$refs[`slide-${slides[index]._id}`][0]
            $slideEl.src = file
          })
          .catch(err => {
            // Retry loading image
            if (counter < 3) {
              setTimeout(() => {
                counter++
                $vm.loadSlide(index, slides, counter)
              }, Math.floor(Math.random() * (5000 - 2500 + 1) + 2500))
            } else {
              console.error(err)
            }
          })
      }
    },
    fullscreen (id) {
      const $vm = this
      $vm.slider = id // Set slider id
      const slider = $vm.$refs[`slider-${id}`] // Get slider element
      $vm.$refs[`slides-container-${id}`].swiper.keyboard.enable() // Enable keyboard navigation
      // Trigger fullscreen for slider element
      if (slider.requestFullscreen) {
        slider.requestFullscreen()
      } else if (slider.mozRequestFullScreen) { /* Firefox */
        slider.mozRequestFullScreen()
      } else if (slider.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
        slider.webkitRequestFullscreen()
      } else if (slider.msRequestFullscreen) { /* IE/Edge */
        slider.msRequestFullscreen()
      }
    },
    exitFullscreen () { // Trigger fullscreen exit
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) { /* Firefox */
        document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) { /* Chrome, Safari and Opera */
        document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) { /* IE/Edge */
        document.msExitFullscreen()
      }
    },
    onFullscreenChange () {
      const $vm = this
      const id = $vm.slider
      if (id) {
        const slider = $vm.$refs[`slider-${id}`] // Get slider element
        slider.classList.toggle('sliderFullscreen') // Add/Remove fullscreen class
        $vm.isFullscreen = !$vm.isFullscreen
        if (!$vm.isFullscreen) {
          $vm.$refs[`slides-container-${id}`].swiper.keyboard.disable() // Disable keyboard navigation
          $vm.slider = null // Reset slider
        }
      }
    }
  }
}
</script>

<style lang="scss">
  @import "../styles/theme";
  .slider {
    cursor: pointer;
    margin-right: -$content-padding;
    margin-left: -$content-padding;
    @media screen and (max-width: $small) {
      margin-right: -$content-padding-mobile;
      margin-left: -$content-padding-mobile;
    }
  }
  .slider-spinner {
    position: absolute;
    top: calc(50% - 20px);
    left: calc(50% - 20px);
    z-index: 10;
  }
  .sliderFullscreen {
    width: 100vw;
    height: 100vh;
    img {
      height: 100%;
      width: auto !important;
    }
    .thumbs-container .swiper-slide {
      width: auto !important;
    }
  }
  .slides-container {
    height: 80%;
    width: 100%;
    background-color: $grey-dark;
    border-top: 1px solid $border;
    border-bottom: 1px solid $grey;
    text-align: center;
    img {
      width: 60%;
      @media screen and (max-width: $small) {
        width: 80%;
      }
      @media screen and (max-width: $xsmall) {
        width: 100%;
      }
    }
  }
  .thumbs-container {
    background-color: #2E3232;
    height: 20%;
    box-sizing: border-box;
    border-bottom: 1px solid $border;
    img {
      width: 100%;
    }
  }
  .thumbs-container .swiper-slide {
    opacity: 0.4;
    height: 100%;
    width: 12.5%;
    @media screen and (max-width: $small) {
      width: 20%;
    }
    @media screen and (max-width: $xsmall) {
      width: 25%;
    }
  }
  .thumbs-container .swiper-slide-active {
    opacity: 0.9;
  }
  .swiper-pagination-bullet,
  .swiper-pagination-bullet-active {
    background: #333 !important;
  }

  .btn-slideshow {
    position: absolute;
    right: 20px;
    @media screen and (max-width: $xsmall) {
      right: 10px;
    }
    .md-icon {
      color: white !important;
      @media screen and (max-width: $xsmall) {
        color: #333 !important;
      }
    }
  }
</style>
